export enum MapSize {
  XS = "XS",
  S = "S",
  M = "M",
  L = "L"
}

export enum AgendasSize {
  S = "S",
  M = "M",
  L = "L"
}

export interface MapData {
  zoom: number;
  centerLat: number;
  centerLon: number;
}

export interface PerilGroup {
  name: string;
  perils: Peril[];
}

export interface Peril {
  name: string;
  checked?: boolean;
  onClick?: () => void;
  onInfoClick?: () => void;
}

export interface MapLayer {
  name: string;
  children: Children[];
}

export interface GroupedResultFathom {
  [layerId: string]: {
    defended: { [label: string]: AnalysisItem[] };
    undefended: { [label: string]: AnalysisItem[] };
  };
}

export type AnalysisStore = Map<string, Map<string, AnalysisItem>>;

export interface AnalysisItem {
  layerId: string;
  pointId?: string;
  valueLabel: string;
  defendeValueLabel?: string;
  floodProtection?: string;
  relativeRisk?: number;
  riskCategory?: number;
  intensity: number;
  details?: {
    Defended?: string;
    Undefended?: string;
    "Intensity Value"?: any;
  };
  checked?: boolean;
  hailAdditionalDetails?: {};
  additionalDetails?: {};
}

export interface ClimateRiskScoresItems {
  index: string;
  scenarios: any[];
}

export interface ClimateItems {
  id: string;
  climateRiskScores: ClimateRiskScoresItems[];
  coordinates: {
    latitude: number;
    longitude: number;
  };
  errors?: any[];
}

export interface Raster {
  raster: {
    color: string;
    opacity: number;
  };
}

export interface Polygon {
  polygon: {
    fill: string;
    fillOpacity: number;
  };
}

export interface Rule {
  title: string;
  symbolizers: (Raster | Polygon)[];
}

export interface LegendItem {
  layerId: string;
  rules: Rule[];
  title: string;
  subtitle?: string;
  index?: number;
  ruleMap?: any;
}

export interface AnalysisData {
  legendItems: LegendItem[] | string;
  analysisItems: AnalysisItem[] | string;
}

export interface Children {
  id: string;
  name: string;
  group: string;
  category: string;
  description: string;
  checked?: boolean;
  onClick?: () => void;
  onInfoClick?: () => void;
  layerObj?: any;
  layerObjEn?: any;
  layerObjCn?: any;
  layerObjWms?: any;
  oversea?: boolean;
  opacity?: number;
}

export interface LocationData {
  id: string;
  name: string;
  address?: string;
  country?: string;
  county?: string;
  province?: string;
  oversea?: boolean;
  spatial?: string;
  createTime?: string;
  items?: any[];
  code?: string;
  itemCount?: number;
  total: number;
  lat: number;
  lon: number;
  checked: boolean;
  latitude?: number;
  latitudeGcj?: number;
  latitudeWgs?: number;
  longitude?: number;
  longitudeGcj?: number;
  longitudeWgs?: number;
  coordinateSystem?: string;
  comment?: string;
  locationSource: string;
  aggregationUnit: string;
  currency?: string;
}

export interface Item {
  id: string;
  country: string;
  county?: string;
  code?: number;
  latitude: number;
  longitude: number;
  name: string;
  number?: string;
  place?: string;
  province?: string;
  street?: string;
  quality?: number;
  address: string;
  city?: string;
  district?: string;
  location?: string;
}

export interface LocationItem {
  id: string;
  total: number;
  building: number;
  content: number;
  businessInterruption: number;
  propertyIsEnumerable: number;
  propertyDamage: number;
  limit: number;
  deductible: number;
  other: number;
}

export interface LegendData {
  layerId: string;
  title: string;
  subtitle: string;
  rules?: any;
  layerInfo: Children;
}

export interface CrsLayer {
  id: string;
  name: string;
  description: string;
}

export interface LocationSourceOption {
  label: string;
  value: string;
}

export interface DefendedData {
  depth: string;
  floodProtection: string;
  returnPeriod: string;
  valueLabel: string;
  color: string;
}

export interface AnalyseModalData {
  type: "1" | "2";
  selectId?: string;
}

export interface Detail {
  Defended: string;
  Undefended: string;
}

export type R = Record<string, string>;

export type LayerGroupType = Record<string, R>;

export type RV = { [key: string]: { [key: string]: number } };

export interface Fathom {
  layerId: string;
  defended: string;
  undefended: string;
}

type GroupRowTypePro = {
  label: string;
  count: number;
  color?: string;
  __lineDistanceMeters?: number;
  total?: number;
  building?: number;
  content?: number;
  businessInterruption?: number;
  propertyIsEnumerable?: number;
  propertyDamage?: number;
  limit?: number;
  deductible?: number;
  other?: number;
};

export type GroupRowType = Omit<GroupRowTypePro, "propertyIsEnumerable">;

export type GroupTableType = Record<string, GroupRowType[]>;

export type EchartDataType = Record<string, GroupTableType>;

export interface GroupTableFathom {
  [layerId: string]: {
    defended: GroupRowType[];
    undefended: GroupRowType[];
  };
}

export interface HailAdditionalDetails {
  "HailHazard-31-year-historical-data-set": R;
  "HailHazard-64-year-historical-data-set": R;
}

export interface EventsFilterForm {
  fromDateTime: string;
  toDateTime: string;
  isShowAll: boolean;
  type: any[];
  longitude?: number;
  latitude?: number;
  country?: string;
  oversea?: boolean;
  spatial?: string;
  isNotRequest: boolean;
  locationInfo?: any;
}
export interface Extent {
  minimumLongitude: number;
  minimumLatitude: number;
  maximumLongitude: number;
  maximumLatitude: number;
}

export interface EventData {
  id: string;
  name: string;
  type: string;
  eventDateTime?: string;
  updateDateTime?: string;
  extent: Extent;
  source?: string;
  longitude: number;
  latitude: number;
  description?: string;
  layerId: string;
  gridDataUrl?: string;
  hemisphere?: string;
  versions?: unknown[];
  linkedEventIds: string[];
  checked?: boolean;
  opacity?: number;
  layerObj?: any;
}

export type PointChecked = {
  id: string;
  checked: boolean;
  [key: string]: any;
};

export interface GroupLabel {
  color: string;
  opacity: number;
  label: string;
  pointIds: PointChecked[];
  sortIndex: number;
  [key: string]: any;
}

export interface EventLegendData {
  layerId: string;
  title: string;
  subtitle: string;
  rules?: any;
  layerInfo: EventData;
}

export interface SnackBarParams {
  title?: any;
  titleData?: any;
  titleIcon?: any;
  titleIconColor?: any;
  message?: any;
  messageData?: any;
  resMessage?: any;
  footBtn?: any;
  autoHideDuration?: number;
}

// 我的形状
// import type {
//   Feature,
//   LineString,
//   MultiLineString,
//   Polygon as GeojsonPolygon,
//   MultiPolygon
// } from "geojson";

// type SupportedGeometry =
//   | LineString
//   | MultiLineString
//   | GeojsonPolygon
//   | MultiPolygon;

export interface Shape {
  // id: string;
  // name: string;
  // category: "polyline" | "polygon";
  // type?: "Ground" | "Aboveground" | "Underground" | "unknow";
  // feature: Feature<SupportedGeometry>;

  id: string;
  name: string;
  category: string;
  type: string;
  sourceType?: "INPUT" | "IMPORT";
  spatial?: "GCJ02" | "WGS84";
  createTime?: number;
  feature: string;
  hasShapeAnalysed?: boolean;
}

/**
 * 统一的 Polyline 数据模型
 * 用于管理绘制线与形状列表线数据（后续扩展 polygon）
 */
export enum DrawShapeSource {
  LINE = "line",
  SHAPE_LINE = "shape-line",
  POLYGON = "polygon",
  SHAPE_POLYGON = "shape-polygon"
}

export enum DrawShapeState {
  IDLE = "idle",
  SELECTED = "selected",
  EDITING = "editing"
}

export interface DrawShapeData {
  id: string;
  source: DrawShapeSource;
  name: string;
  type?: string;
  coordinates: [number, number][];
  state: DrawShapeState;
  aMapRef?: any; // AMap.Polyline 的引用
  spatial?: "GCJ02" | "WGS84";
  shapeId?: string; // 如果是来自 shape-list 的引用
  hasShapeAnalysed?: boolean; // 形状（线/面）是否已发起过分析（用于重复分析免扣费与禁编辑）
}

export interface LineAnalysisItem {
  layerId: string;
  index: number;
  id?: string;
  pointId?: string;
  checked?: boolean;
  valueLabel: string;
  intensity: number;
  floodProtection?: string;
  details?: {
    [key: string]: string;
  };
  longitude: string;
  latitude: string;
}
